// src/components/LoanCalculationForms/Summary.tsx
import React, {useMemo} from "react";
import {Timestamp} from "firebase/firestore";
import {formatDayjsDate} from "../../../utils/dateUtils";

// shadcn
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";

// icons
import {
    Wallet,
    PiggyBank,
    TrendingUp,
    CalendarDays,
} from "lucide-react";

interface SummaryProps {
    total: number | null;
    monthlyPayment: number | null;
    netProfit: number | null;
    closingDate: string | null;
    creditDate: string;
    earlyClosed: boolean;
    status: string;
    earlyCloseDate: string | null;
    creditDetailClosingDay: Timestamp | null;
}

const formatSom = (v: number) =>
    new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "KGS",
        minimumFractionDigits: 0,
    }).format(v);

type SummaryItemProps = {
    title: string;
    value: string;
    Icon: React.ComponentType<{ className?: string }>;
    valueClassName?: string;
};

const SummaryItem: React.FC<SummaryItemProps> = ({
                                                     title,
                                                     value,
                                                     Icon,
                                                     valueClassName,
                                                 }) => {
    return (
        <Card className="rounded-2xl">
            <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">{title}</p>
                        <p
                            className={`text-lg font-semibold leading-none sm:text-xl ${valueClassName ?? ""}`}
                        >
                            {value}
                        </p>
                    </div>
                    <div className="rounded-xl border bg-muted/40 p-2">
                        <Icon className="h-4 w-4 text-muted-foreground"/>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

const Summary: React.FC<SummaryProps> = ({
                                             total,
                                             monthlyPayment,
                                             netProfit,
                                             closingDate,
                                             creditDate,
                                             earlyClosed,
                                             status,
                                             earlyCloseDate,
                                             creditDetailClosingDay,
                                         }) => {

    const displayDate = useMemo(() => {
        const isClosed =
            earlyClosed ||
            ["closedEarly", "returned", "closed"].includes(status);

        if (!isClosed) return closingDate;

        return (
            earlyCloseDate ||
            (creditDetailClosingDay
                ? creditDetailClosingDay.toDate()
                : closingDate)
        );
    }, [earlyClosed, status, earlyCloseDate, creditDetailClosingDay, closingDate]);

    const monthly: number = ["closedEarly", "returned", "closed"].includes(status)
        ? 0
        : monthlyPayment ?? 0;

    const profitClass =
        netProfit && netProfit > 0
            ? "text-green-600"
            : netProfit && netProfit < 0
                ? "text-red-600"
                : "text-muted-foreground";

    if (
        total === null ||
        monthlyPayment === null ||
        !closingDate ||
        !creditDate
    ) {
        return null;
    }

    return (
        <Card className="rounded-2xl">
            <CardHeader>
                <CardTitle className="text-base sm:text-lg">Итоги</CardTitle>
                <CardDescription>Результаты расчёта кредита</CardDescription>
            </CardHeader>

            <CardContent>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <SummaryItem
                        title="Общая сумма"
                        value={formatSom(total)}
                        Icon={Wallet}
                    />
                    <SummaryItem
                        title="Ежемесячный платёж"
                        value={formatSom(monthly)}
                        Icon={PiggyBank}
                    />
                    <SummaryItem
                        title="Чистая прибыль"
                        value={formatSom(netProfit ?? 0)}
                        Icon={TrendingUp}
                        valueClassName={profitClass}
                    />
                    <SummaryItem
                        title="Дата закрытия"
                        value={formatDayjsDate(displayDate)}
                        Icon={CalendarDays}
                    />
                </div>
            </CardContent>
        </Card>
    );
};

export default Summary;
